import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, LogOut, ShieldCheck, UserPlus } from "lucide-react";
import { api, formatApiError, londonDateTime } from "@/lib/api";
import { inputClass } from "@/lib/constants";

export default function AdminAccount() {
  const [profile, setProfile] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [events, setEvents] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [name, setName] = useState("");
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [emailForm, setEmailForm] = useState({ new_email: "", current_password: "" });
  const [newAdmin, setNewAdmin] = useState({ email: "", name: "" });

  const load = () => {
    api.get("/admin/account/profile").then((r) => { setProfile(r.data); setName(r.data.name || ""); }).catch(() => {});
    api.get("/admin/account/sessions").then((r) => setSessions(r.data)).catch(() => {});
    api.get("/admin/account/security-events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/admin/account/admins").then((r) => setAdmins(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const act = (fn, msg) => fn().then((r) => { toast.success(r.data?.note || msg); load(); }).catch((e) => toast.error(formatApiError(e)));

  const changePassword = () => {
    if (pw.next !== pw.confirm) { toast.error("New passwords do not match."); return; }
    act(() => api.put("/admin/account/password", { current_password: pw.current, new_password: pw.next }), "Password changed.");
    setPw({ current: "", next: "", confirm: "" });
  };

  const changeEmail = () => act(() => api.put("/admin/account/email", emailForm), "Login email updated.");

  const createAdmin = () => {
    if (!window.confirm(`Create an account for ${newAdmin.email}? They will receive a set-password link by email.`)) return;
    act(() => api.post("/admin/account/admins", newAdmin), "Account created.");
    setNewAdmin({ email: "", name: "" });
  };

  const disableAdmin = (a) => {
    if (!window.confirm(`Disable ${a.email}? Their sessions are revoked immediately.`)) return;
    act(() => api.post(`/admin/account/admins/${a.id}/disable`), "Account disabled.");
  };

  return (
    <div data-testid="admin-account-page">
      <h1 className="font-serif text-2xl font-bold text-cream-text">Account &amp; Security</h1>

      <section className="mt-6 rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="account-profile-section">
        <h2 className="font-serif text-lg font-semibold text-cream-text">Your details</h2>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="acc-name" className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">Display name</label>
            <input id="acc-name" value={name} onChange={(e) => setName(e.target.value)} className={`${inputClass} mt-1 w-64`} data-testid="account-name-input" />
          </div>
          <button onClick={() => act(() => api.put("/admin/account/profile", { name }), "Name saved.")} className="min-h-[48px] rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-choco" data-testid="account-name-save">Save name</button>
        </div>
        <p className="mt-3 text-xs text-cream-muted">Signed in as <span className="font-bold text-cream-text">{profile?.email}</span></p>
      </section>

      <section className="mt-6 rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="account-password-section">
        <h2 className="flex items-center gap-2 font-serif text-lg font-semibold text-cream-text"><KeyRound className="h-4 w-4 text-gold" aria-hidden="true" /> Change password</h2>
        <p className="mt-1 text-xs text-cream-muted">Changing your password signs out all other devices.</p>
        <div className="mt-4 grid max-w-md gap-3">
          <div>
            <label htmlFor="pw-current" className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">Current password</label>
            <input id="pw-current" type="password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className={`${inputClass} mt-1 w-full`} data-testid="pw-current" />
          </div>
          <div>
            <label htmlFor="pw-next" className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">New password (10+ characters)</label>
            <input id="pw-next" type="password" autoComplete="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className={`${inputClass} mt-1 w-full`} data-testid="pw-next" />
          </div>
          <div>
            <label htmlFor="pw-confirm" className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">Repeat new password</label>
            <input id="pw-confirm" type="password" autoComplete="new-password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className={`${inputClass} mt-1 w-full`} data-testid="pw-confirm" />
          </div>
          <button onClick={changePassword} disabled={!pw.current || pw.next.length < 10} className="min-h-[48px] rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-choco disabled:opacity-40" data-testid="pw-change-button">Change password</button>
        </div>
      </section>

      <section className="mt-6 rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="account-email-section">
        <h2 className="font-serif text-lg font-semibold text-cream-text">Change login email</h2>
        <div className="mt-4 grid max-w-md gap-3">
          <div>
            <label htmlFor="email-new" className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">New email</label>
            <input id="email-new" type="email" value={emailForm.new_email} onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })} className={`${inputClass} mt-1 w-full`} data-testid="email-new" />
          </div>
          <div>
            <label htmlFor="email-pw" className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">Current password (to confirm it's you)</label>
            <input id="email-pw" type="password" autoComplete="current-password" value={emailForm.current_password} onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })} className={`${inputClass} mt-1 w-full`} data-testid="email-pw" />
          </div>
          <button onClick={changeEmail} disabled={!emailForm.new_email || !emailForm.current_password} className="min-h-[48px] rounded-full border border-choco-line px-5 py-2.5 text-sm font-bold text-cream-text hover:border-gold hover:text-gold disabled:opacity-40" data-testid="email-change-button">Update login email</button>
        </div>
      </section>

      <section className="mt-6 rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="account-sessions-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 font-serif text-lg font-semibold text-cream-text"><ShieldCheck className="h-4 w-4 text-gold" aria-hidden="true" /> Signed-in devices</h2>
          <button onClick={() => { if (window.confirm("Sign out all other devices?")) act(() => api.post("/admin/account/sessions/revoke-others"), "Other sessions signed out."); }}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-choco-line px-4 py-2 text-xs font-bold text-cream-text hover:border-gold hover:text-gold" data-testid="revoke-others-button">
            <LogOut className="h-3.5 w-3.5" aria-hidden="true" /> Sign out other devices
          </button>
        </div>
        <ul className="mt-3 space-y-2 text-sm text-cream-muted">
          {sessions.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-choco-line px-4 py-2.5" data-testid={`session-${s.id}`}>
              <span>{s.current ? "This device" : "Another device"} · signed in {londonDateTime(s.created_at)}{s.expired ? " · expired" : ""}</span>
              {!s.current && (
                <button onClick={() => act(() => api.delete(`/admin/account/sessions/${s.id}`), "Session revoked.")} className="rounded-full border border-red-500/40 px-3 py-1 text-[11px] font-bold text-red-300" data-testid={`revoke-${s.id}`}>Revoke</button>
              )}
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-6 rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="account-admins-section">
        <h2 className="flex items-center gap-2 font-serif text-lg font-semibold text-cream-text"><UserPlus className="h-4 w-4 text-gold" aria-hidden="true" /> Administrator accounts</h2>
        <p className="mt-1 text-xs text-cream-muted">For handover: create the owner's account here — they set their own password via an emailed link. No password is ever shown or sent in chat.</p>
        <ul className="mt-4 space-y-2">
          {admins.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-choco-line px-4 py-2.5 text-sm" data-testid={`admin-row-${a.id}`}>
              <span className="text-cream-text">{a.name || "—"} <span className="text-cream-muted">· {a.email}</span>
                {a.disabled && <span className="ml-2 rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-bold text-red-300">disabled</span>}
                {a.must_set_password && <span className="ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold text-amber-300">awaiting password setup</span>}
              </span>
              {!a.disabled && profile?.email !== a.email && (
                <button onClick={() => disableAdmin(a)} className="rounded-full border border-red-500/40 px-3 py-1 text-[11px] font-bold text-red-300" data-testid={`disable-admin-${a.id}`}>Disable</button>
              )}
            </li>
          ))}
        </ul>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="new-admin-name" className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">Name</label>
            <input id="new-admin-name" value={newAdmin.name} onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} className={`${inputClass} mt-1 w-48`} data-testid="new-admin-name" />
          </div>
          <div>
            <label htmlFor="new-admin-email" className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">Email</label>
            <input id="new-admin-email" type="email" value={newAdmin.email} onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} className={`${inputClass} mt-1 w-64`} data-testid="new-admin-email" />
          </div>
          <button onClick={createAdmin} disabled={!newAdmin.email || newAdmin.name.length < 2} className="min-h-[48px] rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-choco disabled:opacity-40" data-testid="create-admin-button">Create account</button>
        </div>
      </section>

      {events && (
        <section className="mt-6 rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="account-events-section">
          <h2 className="font-serif text-lg font-semibold text-cream-text">Security history</h2>
          <ul className="mt-3 space-y-1 text-xs text-cream-muted">
            {[...events.events.map((e) => ({ at: e.created_at, text: e.event.replace(/_/g, " ") })),
              ...events.account_changes.map((a) => ({ at: a.created_at, text: a.summary })),
              ...events.login_attempts.map((a) => ({ at: a.created_at, text: `sign-in ${a.success ? "succeeded" : "failed"}` }))]
              .sort((a, b) => String(b.at).localeCompare(String(a.at)))
              .slice(0, 20)
              .map((e, i) => <li key={i}>{londonDateTime(e.at)} — {e.text}</li>)}
          </ul>
          {events.events.length + events.account_changes.length + events.login_attempts.length === 0 && (
            <p className="mt-3 text-sm text-cream-muted">No security events recorded yet.</p>
          )}
        </section>
      )}

      <p className="mt-6 rounded-2xl border border-choco-line p-4 text-xs text-cream-muted" data-testid="account-recovery-note">
        <strong className="text-cream-text">Locked out?</strong> Use "Forgot password" on the sign-in page — a reset link is emailed to the account address (visible in the Emails section while email is in test mode). Keep at least one active administrator account at all times.
      </p>
    </div>
  );
}
